import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  DirectionsCar,
  People,
  LocalTaxi,
  VerifiedUser,
  Payment,
  MonetizationOn,
  LocalOffer,
  AttachMoney,
  Notifications,
  Assessment,
  Warning,
  TrackChanges,
  Star,
  Support,
  Settings,
  LocationOn,
  Build,
  Category,
  Report,
  SupervisorAccount,
  Logout,
  Description,
  Cancel,
  QuestionAnswer,
  Gavel,
  CurrencyRupee,
  History,
  ContactPhone,
  Chat,
  Forum,
  Language,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Drivers', icon: <DirectionsCar />, path: '/drivers' },
  { text: 'Manage Driver Documents', icon: <Description />, path: '/driver-documents' },
  { text: 'Passengers', icon: <People />, path: '/passengers' },
  { text: 'Rides', icon: <LocalTaxi />, path: '/rides' },
  { text: 'Verifications', icon: <VerifiedUser />, path: '/verifications' },
  { text: 'Documents', icon: <Description />, path: '/documents' },
  // Transactions and Withdrawals temporarily hidden — recharge model collects
  // cash directly from passenger to driver, so platform doesn't track per-ride
  // transactions or driver withdrawals. Routes still exist (see App.js) so
  // direct URLs work; uncomment these lines to re-add to the sidebar.
  // { text: 'Transactions', icon: <Payment />, path: '/transactions' },
  // { text: 'Withdrawals', icon: <MonetizationOn />, path: '/withdrawals' },
  { text: 'Promo Codes', icon: <LocalOffer />, path: '/promo-codes' },
  { text: 'Pricing', icon: <AttachMoney />, path: '/pricing' },
  { text: 'Notifications', icon: <Notifications />, path: '/notifications' },
  { text: 'Notification History', icon: <History />, path: '/notification-history' },
  { text: 'Emergency Contacts', icon: <ContactPhone />, path: '/emergency-contacts' },
  { text: 'Reports', icon: <Assessment />, path: '/reports' },
  { text: 'Emergency', icon: <Warning />, path: '/emergency' },
  { text: 'Live Tracking', icon: <TrackChanges />, path: '/live-tracking' },
  { text: 'Ratings', icon: <Star />, path: '/ratings' },
  { text: 'Support', icon: <Support />, path: '/support' },
  { text: 'Regions', icon: <LocationOn />, path: '/regions' },
  { text: 'Main Services', icon: <Category />, path: '/main-services' },
  { text: 'Services', icon: <Build />, path: '/services' },
  { text: 'Manage Cancel Reasons', icon: <Cancel />, path: '/cancel-reasons' },
  { text: 'Manage Ratings', icon: <Star />, path: '/rating-feedback' },
  { text: 'Manage Quick Chat', icon: <Chat />, path: '/quick-chat' },
  { text: 'Chat Conversations', icon: <Forum />, path: '/chat-conversations' },
  { text: 'FAQs', icon: <QuestionAnswer />, path: '/faqs' },
  { text: 'Legal Pages', icon: <Gavel />, path: '/legal-pages' },
  { text: 'Recharge', icon: <CurrencyRupee />, path: '/recharge-plans' },
  { text: 'Complaints', icon: <Report />, path: '/complaints' },
  { text: 'Admin Users', icon: <SupervisorAccount />, path: '/admin-users' },
  { text: 'Settings', icon: <Settings />, path: '/settings' },
];

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [websiteManagementOpen, setWebsiteManagementOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ bgcolor: 'primary.main', justifyContent: 'center' }}>
        <Typography variant="h5" fontWeight="bold" color="text.primary">
          Pushpa Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1, py: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'text.primary',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path ? 'text.primary' : 'text.secondary',
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}

        {/* Website Management with Dropdown */}
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => setWebsiteManagementOpen(!websiteManagementOpen)}
            sx={{
              borderRadius: 2,
            }}
          >
            <ListItemIcon
              sx={{
                color: 'text.secondary',
                minWidth: 40,
              }}
            >
              <Language />
            </ListItemIcon>
            <ListItemText
              primary="Website Management"
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: 400,
              }}
            />
            {websiteManagementOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        {/* Website Management Submenu */}
        {websiteManagementOpen && (
          <List component="div" disablePadding>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === '/our-services'}
                onClick={() => navigate('/our-services')}
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'text.primary',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'text.primary',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === '/our-services' ? 'text.primary' : 'text.secondary',
                    minWidth: 40,
                  }}
                >
                  <Build />
                </ListItemIcon>
                <ListItemText
                  primary="Our Services"
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: location.pathname === '/our-services' ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
            <Avatar sx={{ bgcolor: 'primary.main', color: 'text.primary' }}>
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.name || 'Admin User'}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.email || 'admin@pushpa.com'}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
